"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAction, useConvexAuth, useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import type { ShipmentQuote } from "@convex/schemas/validators/shipmentQuote";
import type { CheckoutShipment } from "@convex/schemas/validators/checkoutShipment";
import { handleError } from "@/lib/handle-error";
import { readPosthogDistinctId } from "@/lib/analytics/posthog-identity";
import { useOnceUpload } from "@/hooks/use-once-upload";
import {
  EMPTY_PURCHASE_ORDER,
  isPurchaseOrderDraftReady,
  type PurchaseOrderDraft,
} from "@/components/orders/purchase-order-fields";
import { deliveryFromShipments, type DirectCheckoutRequest } from "./use-direct-checkout";

export type PaymentChoice = "card" | "terms";

export type CheckoutTermsDecision = FunctionReturnType<
  typeof api.credit.checkoutTerms.getCheckoutTermsOption
>["decision"];

export interface TermsLoad {
  amountCents: number;
  deliveryAddress: { line1?: string; city?: string; state?: string; postalCode?: string };
}

export function termsLoadsFor(
  shipments: ReadonlyArray<CheckoutShipment>,
  shipmentQuotes: ReadonlyArray<ShipmentQuote> | undefined,
): TermsLoad[] | undefined {
  if (!shipmentQuotes || shipmentQuotes.length === 0) return undefined;
  const byId = new Map(shipments.map((shipment) => [shipment.shipmentId, shipment]));
  const loads: TermsLoad[] = [];
  for (const quote of shipmentQuotes) {
    const shipment = byId.get(quote.shipmentId);
    if (!shipment) return undefined;
    loads.push({
      amountCents: quote.subtotalCents + quote.freightCents,
      deliveryAddress: {
        line1: shipment.line1 || undefined,
        city: shipment.city,
        state: shipment.state,
        postalCode: shipment.postalCode,
      },
    });
  }
  return loads;
}

function usePlaceOnTerms(purchaseOrder: PurchaseOrderDraft) {
  const router = useRouter();
  const startOnTerms = useAction(api.cart.termsCheckout.startCartCheckoutOnTerms);
  const generateUploadUrl = useMutation(api.files.mutations.generateUploadUrl);
  const uploadOnce = useOnceUpload(generateUploadUrl);
  const [termsIdempotencyKey] = useState(() => crypto.randomUUID());
  const [isPlacing, setIsPlacing] = useState(false);

  const place = async (request: DirectCheckoutRequest): Promise<boolean> => {
    if (request.shipments.length === 0 || !purchaseOrder.file) return false;
    setIsPlacing(true);
    try {
      const storageId = await uploadOnce(purchaseOrder.file);
      const placed = await startOnTerms({
        ...deliveryFromShipments(request.shipments),
        shipments: request.shipments,
        productIds:
          request.source === "listing" ? request.items.map((item) => item.productId) : undefined,
        expectedFreightCents: request.expectedFreightCents,
        expectedProductSubtotals: request.expectedProductSubtotals,
        posthogDistinctId: readPosthogDistinctId(),
        termsIdempotencyKey,
        purchaseOrder: { number: purchaseOrder.number, storageId },
      });
      router.push(`/cart/checkout/success?attempt_id=${placed.checkoutAttemptId}`);
      return true;
    } catch (thrown) {
      handleError(thrown, {
        display: "banner",
        fallbackMessage: "Could not place the order on terms",
        reportIssueContext: { titlePrefix: "Terms checkout failed" },
      });
      setIsPlacing(false);
      return false;
    }
  };

  return { place, isPlacing };
}

function totalOf(loads: ReadonlyArray<TermsLoad> | undefined): number | undefined {
  return loads?.reduce((sum, load) => sum + load.amountCents, 0);
}

export function termsLabelOf(decision: CheckoutTermsDecision | undefined): string | undefined {
  if (decision?.kind !== "terms") return undefined;
  const labels = [...new Set(decision.loads.map((load) => load.label))];
  return labels.length === 1 ? labels[0] : "your approved terms";
}

export function useTermsPayment(loads: TermsLoad[] | undefined) {
  const { isAuthenticated } = useConvexAuth();
  const result = useQuery(
    api.credit.checkoutTerms.getCheckoutTermsOption,
    isAuthenticated ? { loads: loads ?? [] } : "skip",
  );
  const [choice, setChoice] = useState<PaymentChoice>("card");
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderDraft>(EMPTY_PURCHASE_ORDER);
  const { place, isPlacing } = usePlaceOnTerms(purchaseOrder);
  const amountCents = totalOf(loads) ?? 0;
  const decision = result && result.amountCents === amountCents ? result.decision : undefined;
  const onTerms = choice === "terms";
  const termsUsable = decision?.kind === "terms";
  return {
    isGuest: !isAuthenticated,
    resolving: isAuthenticated && loads !== undefined && decision === undefined,
    decision,
    choice,
    setChoice,
    purchaseOrder,
    setPurchaseOrder,
    onTerms,
    readyOnTerms: onTerms && termsUsable && isPurchaseOrderDraftReady(purchaseOrder),
    termsLabel: termsLabelOf(decision),
    place,
    isPlacing,
  };
}

export type TermsPayment = ReturnType<typeof useTermsPayment>;
